import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Settings2, Scale, X as CloseIcon } from "lucide-react";
import Modal, { Field, Input, Textarea, Select, GoldButton, PageHeader } from "@/components/Ui";

const CYCLES = [
    { value: "quarterly", label: "Quarterly" },
    { value: "half_yearly", label: "Half-Yearly" },
    { value: "annual", label: "Annual" },
    { value: "bi_annual", label: "Bi-Annual" },
    { value: "tri_annual", label: "Tri-Annual" },
];

/**
 * Grouped checkbox catalog for plan features. Catalog is fetched from the
 * backend so we have a single source of truth for both the create-plan form
 * and the per-plan flag editor.
 */
function FeatureCatalogGrid({ catalog, value, onChange, onBulk, testIdPrefix }) {
    if (!catalog || catalog.length === 0) {
        return <div className="text-xs text-muted-foreground italic">Loading feature catalog…</div>;
    }
    const grouped = catalog.reduce((acc, f) => {
        (acc[f.category] = acc[f.category] || []).push(f);
        return acc;
    }, {});
    const enableAll = () => {
        const next = {};
        catalog.forEach(f => { next[f.key] = true; });
        onBulk(next);
    };
    const disableAll = () => {
        const next = {};
        catalog.forEach(f => { next[f.key] = false; });
        onBulk(next);
    };
    return (
        <div>
            <div className="flex gap-2 mb-3 text-[10px] uppercase tracking-[0.16em]">
                <button type="button" onClick={enableAll} data-testid={`${testIdPrefix}-enable-all`}
                    className="px-3 py-1.5 border border-border text-muted-foreground hover:text-primary hover:border-primary">Enable all</button>
                <button type="button" onClick={disableAll} data-testid={`${testIdPrefix}-disable-all`}
                    className="px-3 py-1.5 border border-border text-muted-foreground hover:text-rose-400 hover:border-rose-400">Disable all</button>
            </div>
            <div className="space-y-4">
                {Object.entries(grouped).map(([cat, items]) => (
                    <div key={cat} className="border border-border">
                        <div className="px-3 py-2 bg-muted overline">{cat}</div>
                        <div className="divide-y divide-border">
                            {items.map(f => {
                                const on = value[f.key] !== undefined ? value[f.key] !== false : f.default;
                                return (
                                    <label key={f.key} className="flex items-center justify-between px-3 py-2.5 cursor-pointer hover:bg-muted/50">
                                        <div className="text-sm">{f.label}
                                            <span className="text-[10px] text-muted-foreground font-mono ml-2">{f.key}</span>
                                        </div>
                                        <input type="checkbox" checked={on}
                                            onChange={e => onChange(f.key, e.target.checked)}
                                            data-testid={`${testIdPrefix}-${f.key}`}
                                            className="w-4 h-4 accent-primary"/>
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function AdminPlans() {
    const [items, setItems] = useState([]);
    const [catalog, setCatalog] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", description: "", price: 0, billing_cycle: "annual", flags: {}, max_users: 5, is_active: true });
    const [saving, setSaving] = useState(false);
    const [flagPlan, setFlagPlan] = useState(null);
    const [flagState, setFlagState] = useState({});
    const [savingFlags, setSavingFlags] = useState(false);
    // ---- Phase 9: eCourts endpoint allowlist editor ----
    const [ecPlan, setEcPlan] = useState(null);       // plan being edited
    const [ecCatalog, setEcCatalog] = useState([]);   // [{key,label,category}]
    const [ecAllowed, setEcAllowed] = useState(null); // null = no gating, [] = block all
    const [savingEc, setSavingEc] = useState(false);

    const load = useCallback(async () => {
        try {
            const [plans, cat] = await Promise.all([
                api.get("/plans"),
                api.get("/platform/features/catalog"),
            ]);
            setItems(plans.data);
            setCatalog(cat.data || []);
        } catch (e) { toast.error(apiError(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);  // eslint-disable-line

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            // Derive a human-readable features[] from the enabled flag labels
            // (used for the public marketing card). Flags are also persisted
            // separately for runtime gating.
            const enabledFeatures = catalog
                .filter(f => form.flags[f.key] !== false && (form.flags[f.key] === true || f.default))
                .map(f => f.label);
            await api.post("/plans", {
                name: form.name,
                description: form.description,
                price: Number(form.price),
                billing_cycle: form.billing_cycle,
                max_users: Number(form.max_users),
                is_active: form.is_active,
                features: enabledFeatures,
            });
            // Plans API doesn't accept `flags` on create — push them in a follow-up call
            const refreshed = await api.get("/plans");
            const created = (refreshed.data || []).find(p => p.name === form.name);
            if (created && Object.keys(form.flags).length > 0) {
                await api.patch(`/platform/plans/${created.id}/flags`, { flags: form.flags });
            }
            toast.success("Plan created"); setOpen(false); load();
            setForm({ name: "", description: "", price: 0, billing_cycle: "annual", flags: {}, max_users: 5, is_active: true });
        } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    };

    const openCreate = () => {
        // Pre-populate flags from the catalog defaults each time the modal opens
        const initial = {};
        catalog.forEach(f => { initial[f.key] = f.default; });
        setForm(f => ({ ...f, flags: initial }));
        setOpen(true);
    };

    const remove = async (id) => {
        if (!window.confirm("Delete plan?")) return;
        try { await api.delete(`/plans/${id}`); load(); } catch (e) { toast.error(apiError(e)); }
    };

    const openFlags = (p) => { setFlagPlan(p); setFlagState(p.flags || {}); };
    const closeFlags = () => { setFlagPlan(null); setFlagState({}); };
    const saveFlags = async () => {
        if (!flagPlan) return;
        setSavingFlags(true);
        try {
            await api.patch(`/platform/plans/${flagPlan.id}/flags`, { flags: flagState });
            toast.success(`Features updated for ${flagPlan.name}`);
            await load();
            closeFlags();
        } catch (e) { toast.error(apiError(e)); } finally { setSavingFlags(false); }
    };

    const openEc = async (p) => {
        setEcPlan(p);
        setEcAllowed(Array.isArray(p.ecourts_endpoints_allowed) ? [...p.ecourts_endpoints_allowed] : null);
        if (ecCatalog.length === 0) {
            try {
                const { data } = await api.get("/platform/ecourts/endpoints-catalog");
                setEcCatalog(data);
            } catch (e) { toast.error(apiError(e)); }
        }
    };
    const closeEc = () => { setEcPlan(null); setEcAllowed(null); };
    const toggleEc = (key) => {
        const curr = ecAllowed === null ? ecCatalog.map(c => c.key) : ecAllowed;
        const next = curr.includes(key) ? curr.filter(k => k !== key) : [...curr, key];
        setEcAllowed(next);
    };
    const saveEc = async () => {
        if (!ecPlan) return;
        setSavingEc(true);
        try {
            await api.patch(`/platform/plans/${ecPlan.id}/ecourts`, { ecourts_endpoints_allowed: ecAllowed });
            toast.success(`eCourts allowlist saved for ${ecPlan.name}`);
            await load();
            closeEc();
        } catch (e) { toast.error(apiError(e)); } finally { setSavingEc(false); }
    };

    return (
        <>
            <PageHeader
                title="Plans"
                subtitle="Subscription tiers offered to tenants. Toggle which features each plan unlocks."
                action={<GoldButton onClick={openCreate} data-testid="plans-add-btn"><Plus className="w-4 h-4 inline mr-2" strokeWidth={2}/>Add plan</GoldButton>}
            />
            <div className="grid md:grid-cols-3 gap-px bg-border">
                {items.map(p => (
                    <div key={p.id} className="bg-background p-8 relative" data-testid={`plan-card-${p.id}`}>
                        <div className="absolute top-4 right-4 flex items-center gap-2">
                            <button onClick={() => openEc(p)} data-testid={`plan-ecourts-${p.id}`}
                                title="eCourts endpoint allowlist"
                                className="text-muted-foreground hover:text-primary"><Scale className="w-4 h-4" strokeWidth={1.5}/></button>
                            <button onClick={() => openFlags(p)} data-testid={`plan-flags-${p.id}`}
                                title="Edit features"
                                className="text-muted-foreground hover:text-primary"><Settings2 className="w-4 h-4" strokeWidth={1.5}/></button>
                            <button onClick={() => remove(p.id)} data-testid={`plan-delete-${p.id}`}
                                title="Delete plan"
                                className="text-muted-foreground hover:text-red-400"><Trash2 className="w-4 h-4" strokeWidth={1.5}/></button>
                        </div>
                        <div className="overline mb-3">{p.name}</div>
                        {p.is_custom_pricing || p.price === null || p.price === undefined ? (
                            <>
                                <div className="font-serif text-4xl mb-1" data-testid={`plan-price-${p.id}`}>Custom</div>
                                <div className="text-xs text-muted-foreground mb-6">/ contact sales</div>
                            </>
                        ) : (
                            <>
                                <div className="font-serif text-4xl mb-1" data-testid={`plan-price-${p.id}`}>₹{Number(p.price || 0).toLocaleString("en-IN")}</div>
                                <div className="text-xs text-muted-foreground mb-6">/ {String(p.cycle || p.billing_cycle || "monthly").replace(/_/g, " ")}</div>
                            </>
                        )}
                        <p className="text-sm text-muted-foreground mb-6">{p.description}</p>
                        <ul className="space-y-2 text-sm mb-6">
                            {p.features.map((f, i) => (
                                <li key={`${i}-${f}`} className="flex items-start gap-2"><span className="w-1 h-1 bg-primary mt-2 shrink-0"></span><span>{f}</span></li>
                            ))}
                        </ul>
                        <div className="text-xs text-muted-foreground font-mono mb-3">Max users: {p.max_users}</div>
                        {p.flags && Object.keys(p.flags).length > 0 && (
                            <div className="text-[10px] text-muted-foreground uppercase tracking-[0.2em] mt-3 pt-3 border-t border-border">
                                {Object.entries(p.flags).filter(([, v]) => v).length} feature override(s)
                            </div>
                        )}
                        {Array.isArray(p.ecourts_endpoints_allowed) && (
                            <div className="text-[10px] text-primary uppercase tracking-[0.2em] mt-2 inline-flex items-center gap-1">
                                <Scale className="w-3 h-3" strokeWidth={1.5}/>
                                eCourts gated · {p.ecourts_endpoints_allowed.length} endpoint(s)
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <Modal open={open} onClose={() => setOpen(false)} title="Create plan" size="lg" testId="plan-modal">
                <form onSubmit={submit} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Name"><Input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="plan-name-input"/></Field>
                        <Field label="Price (₹)"><Input required type="number" value={form.price} onChange={e => setForm({...form, price: e.target.value})} data-testid="plan-price-input"/></Field>
                        <Field label="Billing cycle"><Select value={form.billing_cycle} onChange={e => setForm({...form, billing_cycle: e.target.value})} options={CYCLES} data-testid="plan-cycle-select"/></Field>
                        <Field label="Max users"><Input type="number" value={form.max_users} onChange={e => setForm({...form, max_users: e.target.value})}/></Field>
                    </div>
                    <Field label="Description"><Textarea rows="2" value={form.description} onChange={e => setForm({...form, description: e.target.value})}/></Field>

                    <div>
                        <div className="overline mb-2">Features included</div>
                        <p className="text-[11px] text-muted-foreground mb-3">Tick everything this plan unlocks. Tenants on this plan only see / can call the ticked features.</p>
                        <FeatureCatalogGrid catalog={catalog}
                            value={form.flags}
                            onChange={(k, v) => setForm({ ...form, flags: { ...form.flags, [k]: v } })}
                            onBulk={(next) => setForm({ ...form, flags: next })}
                            testIdPrefix="create-plan"/>
                    </div>

                    <div className="flex justify-end pt-2"><GoldButton disabled={saving} data-testid="plan-save-btn">{saving ? "Creating…" : "Create plan"}</GoldButton></div>
                </form>
            </Modal>

            {flagPlan && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 p-4" onClick={closeFlags}>
                    <div className="bg-background border border-border w-full max-w-2xl p-6" onClick={e => e.stopPropagation()} data-testid="plan-flags-modal">
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <div className="overline">Plan features</div>
                                <div className="font-serif text-xl">{flagPlan.name}</div>
                                <p className="text-xs text-muted-foreground mt-2">Toggle off to hide that module from every tenant on this plan. Disabled features disappear from the tenant sidebar.</p>
                            </div>
                            <button onClick={closeFlags} className="text-muted-foreground hover:text-foreground" data-testid="plan-flags-close">
                                <CloseIcon className="w-5 h-5" strokeWidth={1.5}/>
                            </button>
                        </div>
                        <div className="max-h-[60vh] overflow-y-auto">
                            <FeatureCatalogGrid catalog={catalog}
                                value={flagState}
                                onChange={(k, v) => setFlagState({ ...flagState, [k]: v })}
                                onBulk={setFlagState}
                                testIdPrefix="plan-flag"/>
                        </div>
                        <div className="mt-6 flex justify-end gap-3">
                            <button onClick={closeFlags} className="text-sm text-muted-foreground hover:text-foreground px-4 py-2">Cancel</button>
                            <GoldButton onClick={saveFlags} disabled={savingFlags} data-testid="plan-flags-save">{savingFlags ? "Saving…" : "Save features"}</GoldButton>
                        </div>
                    </div>
                </div>
            )}
            {ecPlan && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 p-4" onClick={closeEc}>
                    <div className="bg-background border border-border w-full max-w-xl p-6" onClick={e => e.stopPropagation()} data-testid="plan-ecourts-modal">
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <div className="overline">eCourts endpoint allowlist</div>
                                <div className="font-serif text-xl">{ecPlan.name}</div>
                                <p className="text-xs text-muted-foreground mt-2">
                                    Tick the eCourts endpoints unlocked on this plan. <b className="text-primary">No gating</b> = all endpoints allowed (legacy behaviour). Empty selection = block all eCourts calls on this plan.
                                </p>
                            </div>
                            <button onClick={closeEc} className="text-muted-foreground hover:text-foreground" data-testid="plan-ecourts-close">
                                <CloseIcon className="w-5 h-5" strokeWidth={1.5}/>
                            </button>
                        </div>

                        <div className="flex gap-2 mb-3 text-[10px] uppercase tracking-[0.16em]">
                            <button onClick={() => setEcAllowed(null)}
                                data-testid="plan-ecourts-nogating"
                                className={`px-3 py-1.5 border ${ecAllowed === null ? "border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
                                No gating (allow all)
                            </button>
                            <button onClick={() => setEcAllowed(ecCatalog.map(c => c.key))}
                                data-testid="plan-ecourts-all"
                                className="px-3 py-1.5 border border-border text-muted-foreground hover:text-foreground">
                                Select all
                            </button>
                            <button onClick={() => setEcAllowed([])}
                                data-testid="plan-ecourts-none"
                                className="px-3 py-1.5 border border-border text-muted-foreground hover:text-foreground">
                                Clear
                            </button>
                        </div>

                        <div className="divide-y divide-border max-h-[55vh] overflow-y-auto border border-border">
                            {ecCatalog.map(c => {
                                const checked = ecAllowed === null ? true : ecAllowed.includes(c.key);
                                const disabled = ecAllowed === null;
                                return (
                                    <label key={c.key} className={`flex items-center justify-between px-3 py-2.5 cursor-pointer ${disabled ? "opacity-60" : ""}`}>
                                        <div>
                                            <div className="text-sm text-foreground">{c.label}</div>
                                            <div className="text-[10px] text-muted-foreground font-mono">{c.key} · {c.category}</div>
                                        </div>
                                        <input type="checkbox" checked={checked}
                                            onChange={() => toggleEc(c.key)}
                                            disabled={disabled}
                                            data-testid={`plan-ecourts-${c.key}`}
                                            className="w-4 h-4 accent-primary"/>
                                    </label>
                                );
                            })}
                        </div>
                        <div className="mt-6 flex justify-end gap-3">
                            <button onClick={closeEc} className="text-sm text-muted-foreground hover:text-foreground px-4 py-2">Cancel</button>
                            <GoldButton onClick={saveEc} disabled={savingEc} data-testid="plan-ecourts-save">
                                {savingEc ? "Saving…" : "Save allowlist"}
                            </GoldButton>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
